<script setup lang="ts">
    /**
     * Hands a sheet's swipe-to-dismiss to the backdrop layers (`useSheetSwipeBackdrop`).
     *
     * Its own component, mounted inside reka-ui's DrawerRoot, because that is the only
     * place its root context can be injected from: UiActionSheet renders DrawerRoot itself
     * and so sits above the provider it would need.
     *
     * `nestedSwipeProgressStore` is exported but named for nested drawers, so it is read
     * defensively: should a reka-ui upgrade drop it, this does nothing and the backdrop
     * simply keeps the plain fade it had before.
     */
    import { injectDrawerRootContext } from 'reka-ui';
    import useSheetSwipeBackdrop from '~/composables/useSheetSwipeBackdrop';

    const props = defineProps<{
        /** Identifies the sheet in `SheetBackdropState.owner`. */
        ownerId: string;
        /** See `SheetBackdropState.syncGlobal`. Read at the start of each gesture. */
        syncGlobalBackdrop: () => boolean;
        /** The sheet's content element, which reka writes `--drawer-swipe-strength` on. */
        getContent: () => HTMLElement | null;
    }>();

    const { sheetBackdrop } = useModalsStore();
    const context = injectDrawerRootContext(null);
    const progress = context?.nestedSwipeProgressStore;

    if (context && progress) {
        useSheetSwipeBackdrop(
            {
                progress,
                isSwiping: context.isSwiping,
                open: context.open,
                readSwipeStrength: () => parseFloat(props.getContent()?.style.getPropertyValue('--drawer-swipe-strength') ?? '') || 1,
            },
            sheetBackdrop,
            {
                ownerId: props.ownerId,
                syncGlobal: props.syncGlobalBackdrop,
                reducedMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
            }
        );
    }
</script>

<template>
    <!-- Renderless: this component only wires state. Nothing is ever slotted in, so
         this renders nothing; it is here because a template needs a root. -->
    <slot />
</template>
